
import { FC, useState, ChangeEventHandler, MouseEventHandler } from 'react';
import { PrimaryButton, FileUpload, CloseButton } from '@cb/apricot-react';
import { Grid } from '@mui/material';

import { FieldRow, FieldLabel } from 'src/generic/component';
import { Input } from 'src/generic/component/form';
import { Typography } from 'src/components';

interface FileUploaderProps {
  acceptFileTypes?: string;
  label?: string;
  readOnly?: boolean;
  required?: boolean;
  uploadButton?: string;
  onUploadFile: (file: File, description: string | undefined) => Promise<void>;
}

export const FileUploader: FC<FileUploaderProps> = ({
  acceptFileTypes,
  uploadButton,
  label,
  readOnly,
  required,
  onUploadFile,
}) => {
  const [error, setError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [description, setDescription] = useState<string | undefined>(undefined);

  const handleFileRemoved = () => {
    setFile(null);
  };

  const handleFileSelected = (event: any) => {
    setFile(event.data.file);
  };

  const handleChangeDescription: ChangeEventHandler<HTMLInputElement> = (event) => {
    setDescription(event.target.value || undefined);
  };

  const reset = () => {
    setFile(null);
    setDescription(undefined);
    setError('');
  };

  const handleUploadFile = async () => {
    if (!file || !onUploadFile) {
      return;
    }

    try {
      setError(undefined);
      setLoading(true);
      await onUploadFile(file, description);
      reset();
    } catch (error: any) {
      setError(error && error.message ? error.message : error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * This code is required due to the component opening the window repeatedly from click events
   * not being handled properly by the apricot component.  We are forced to do some pseudo tracking
   * of amount of clicks to try to identify the opening click vs the selecting click.  This should be
   * fixed on apricot's side.
   */
  const [count, setCount] = useState(0);
  const handleClick: MouseEventHandler = (event) => {
    if (count % 2) {
      event.preventDefault();
    }
    setCount((count) => count + 1);
  };

  return (
    <Grid container>
      {label && (
        <Grid item xs={12}>
          <Typography hasError={error ? true : false} variant="body1">{`${label} ${required ? '*' : ''}`}</Typography>
        </Grid>
      )}

      {file ? (
        <Grid item xs={12}>
          <div style={{ fontWeight: 500 }}>Enter description to finish upload...</div>
          <div>{file.name}
            <CloseButton onClick={() => { reset(); }} />
          </div>
          <FieldRow>
            <FieldLabel variant="body1">Description</FieldLabel>
            <Input
              disabled={readOnly || loading}
              value={description}
              onChange={handleChangeDescription}
            />
          </FieldRow>
          <FieldRow>
            <PrimaryButton disabled={loading} style={{ marginLeft: 8 }} onClick={handleUploadFile}>
              Upload
            </PrimaryButton>
          </FieldRow>

          {error && (
            <Grid item xs={12}>
              <span style={{ fontWeight: 'normal' }} className='cb-input-helper cb-validation-error'>{error}</span>
            </Grid>
          )}
        </Grid>
      ) : <FileUpload
        id="fileUpload"
        label={uploadButton ?? 'Add picture'}
        // @ts-ignore
        accept={acceptFileTypes ?? 'image/*,application/pdf'}
        disabled={readOnly ?? loading}
        onClick={handleClick}
        onFileRemoved={handleFileRemoved}
        onFileSelected={handleFileSelected}
      />
      }
    </Grid >
  );
};